import { HttpClient, HttpParams } from '@angular/common/http'
import { inject, Injectable, Signal, signal, WritableSignal } from '@angular/core'
import { SaleActions } from 'app/interfaces/sale-actions.interface';
import { Display } from 'app/interfaces/display.interface';
import { environment } from 'environments/environment'
import { debounceTime, map, Observable, of } from 'rxjs'
import { ApiService } from 'app/services/api.service';
import { Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'

@Injectable({
    providedIn: 'root',
})
export class DisplaysService {
    public _displays: WritableSignal<Display[]> = signal<Display[]>([]);
    public readonly displays: Signal<Display[]> = this._displays.asReadonly();

    private _hasNextPage: WritableSignal<boolean> = signal<boolean>(false);
    public readonly hasNextPage: Signal<boolean> = this._hasNextPage.asReadonly();

    private _loaded: WritableSignal<boolean> = signal<boolean>(false);
    public readonly loaded: Signal<boolean> = this._loaded.asReadonly();

    private _httpClient!: HttpClient;

    constructor(private api: ApiService, private router: Router, private translate: TranslateService) {
        this._httpClient = inject(HttpClient);
    }

    private translations(){
        this.translate.get([
            'my_sales.complete1',
            'my_displays.remove',
            'my_displays.draft',
            'my_displays.cta_draft',
            'my_displays.draft_title',
            'my_displays.draft_subtitle',
            'my_displays.points_to_validate',
            'my_displays.in_process',
            'my_displays.display_thanks',
            'my_displays.display_soon_our_team',
            'my_displays.display_confirmed_will_install_on',
            'my_displays.display_once_installed',
            'my_displays.display_change_image',
            'my_displays.display_request_delete_btn',
            'my_displays.approved',
            'my_displays.display_installation_confirmed',
            'my_displays.display_thanks_confidence',
            'my_displays.activate_warranty',
            'my_displays.send_instructions',
            'my_displays.outdated',
            'my_displays.cta_approved',
            'my_displays.approved_subtitle',
            'my_displays.generated_points',
            'my_displays.display_cancel_delete_btn',
            'my_displays.display_timeline_deleting_status',
            'my_displays.display_processing_deletion',
            'my_displays.display_deletion_help_text'
        ])
    }

    public loadDisplaysList(status: string | null = null, page: number = 0, size: number = 10, sort: string = 'id,desc', search: string | null = null): Observable<Display[]> {
        let params = new HttpParams().set('page', page.toString()).set('size', size.toString()).set('sort', sort);

        if (status && status !== 'all') {
            params = params.set('status', status.toUpperCase() ?? '');
        }
        if (search) {
            params = params.set('search', search ?? '');
        }

        return this._httpClient.get(`${environment.API_URL}/api/displays`, { params }).pipe(
            map((response: any) => {
              if (page === 0) {
                this._displays.set(response.displays as Display[]);
              } else {
                this._displays.update((displays: Display[]) => [...displays, ...(response.displays as Display[])]);
              }
              this._hasNextPage.set(response.hasNextPage ?? false);
              this._loaded.set(true);
              return response.displays as Display[];
            })
        )
    }

    public removeDisplay(display: Display): Observable<void> {
      if(display.status === 'DRAFT') {
        this.api.myDisplays__deleteDisplay(display.id).subscribe({
          next: (response) => {
            console.log("Display deleted successfully - ID: " + display.id);
            window.location.reload();
          },
          error: (error) => {
            console.error('Error deleting display:', error);
          }
        });
      }
      return of();
    }

    public requestDeletion(display: Display): Observable<void> {
      if (display.status === 'INSTALLED') {
        this.api.myDisplays__requestDeletion(display.id).subscribe({
          next: () => {
            console.log("Deletion request sent for display " + display.id);
            window.location.reload();
          },
          error: (error) => {
            console.error('Error requesting deletion:', error);
          }
        });
      }
      return of();
    }

    public cancelDeletion(display: Display): Observable<void> {
      if (display.deleteRequested) {
        this.api.myDisplays__cancelRequestDeletion(display.id).subscribe({
          next: () => {
            console.log("Deletion request cancelled for display " + display.id);
            window.location.reload();
          },
          error: (error) => {
            console.error('Error cancelling deletion:', error);
          }
        });
      }
      return of();
    }

    public completeDisplay(displayId: string): Observable<void> {
      this.router.navigate(['/register-display'], { queryParams: { displayId } });
      return of();
    }

    public changeImage(displayId: string): Observable<void> {
      return of();
    }

    public requestRenewal(displayToRenew: string): Observable<void> {
      this.router.navigate(['/register-display'], { queryParams: { displayToRenew } });
      return of();
    }

    public getDisplayActions(display: Display) {
      console.log("GET display actions - status: " + display.status);
      let effectiveStatus = display.status;
      if (display.deleteRequested) {
        effectiveStatus = 'DELETION_REQUESTED';
      }
      const displaysStatus: any = {
          DRAFT: {
              primaryButton: {
                  actionMessage: 'my_sales.complete1',
                  actionOperation: 'completeDisplay',
              },
              secondaryButton: {
                  actionMessage: 'my_displays.remove',
                  actionOperation: 'removeDisplay',
              },
              badge: 'my_displays.draft',
              ctaText: 'my_displays.cta_draft',
              title: 'my_displays.draft_title',
              subtitle: 'my_displays.draft_subtitle',
              description: null,
              waitActionMessage: null,
              iconBadgeClass: 'display draft',
              textBadgeClass: 'draft',
              scoreBoxClass: 'gray',
              pointsText: 'my_displays.points_to_validate',
          },
          IN_PROGRESS: {
              badge: 'my_displays.in_process',
              ctaText: '5',
              title: 'my_displays.display_thanks',
              subtitle: 'my_displays.display_soon_our_team',
              description: null,
              waitActionMessage: null,
              iconBadgeClass: 'error',
              textBadgeClass: 'display incomplete',
              scoreBoxClass: 'red',
              pointsText: '9',
          },
          INVOICED: {
              badge: 'my_displays.in_process',
              ctaText: '5',
              title: 'my_displays.display_confirmed_will_install_on',
              subtitle: 'my_displays.display_once_installed',
              description: null,
              waitActionMessage: null,
              iconBadgeClass: 'display_incomplete',
              textBadgeClass: 'display incomplete',
              scoreBoxClass: 'red',
              pointsText: '9',
          },
          INSTALLED: {
              primaryButton: {
                  actionMessage: 'my_displays.display_change_image',
                  actionOperation: 'changeImage',
              },
              secondaryButton: {
                  actionMessage: 'my_displays.display_request_delete_btn',
                  actionOperation: 'requestDeletion',
              },
              badge: 'my_displays.approved',
              ctaText: '5',
              title: 'my_displays.display_installation_confirmed',
              subtitle: 'my_displays.display_thanks_confidence',
              description: null,
              waitActionMessage: null,
              iconBadgeClass: 'delivered',
              textBadgeClass: 'display incomplete',
              scoreBoxClass: 'red',
              pointsText: '9',
          },
          DISCONTINUED: {
              //REWVIEW: boton de renovar display
              primaryButton: {
                  actionMessage: 'menu.FABRICATOR.my-displays',
                  actionOperation: 'requestRenewal',
              },
              secondaryButton: {
                actionMessage: 'my_displays.display_request_delete_btn',
                actionOperation: 'requestDeletion',
              },
              badge: 'my_displays.outdated',
              ctaText: 'my_displays.cta_approved',
              title: 'my_displays.denied',
              subtitle: 'my_displays.display_thanks_confidence',
              description: null,
              waitActionMessage: null,
              iconBadgeClass: 'error',
              textBadgeClass: 'display approved',
              scoreBoxClass: 'gray',
              pointsText: 'my_displays.generated_points',
          },
          DELETION_REQUESTED: {
              primaryButton: {
                  actionMessage: 'my_displays.display_cancel_delete_btn',
                  actionOperation: 'cancelDeletion',
              },
              badge: 'my_displays.display_timeline_deleting_status',
              title: 'my_displays.display_processing_deletion',
              subtitle: 'my_displays.display_deletion_help_text',
          },
      }
      /* if (display.status !== 'DRAFT') {
        const status = {...displaysStatus[display.status] };
        status.primaryButton = {
          actionMessage: 'my_displays.download_warranty',
          actionOperation: 'downloadWarranty',
        };
        status.secondaryButton = null;
        status.ctaText = 'my_displays.display_warranty_activated_cta_text';

        console.log("Display status (not draft): ", status);
        return status;
      } */

      return displaysStatus[effectiveStatus] ?? {};
    }

    public mapApplicationFromSF(application: string) {
      if(application == null){
          return null;
      }

      if(application === "KITCHEN COUNTERTOP") {
          return "kitchen";
      }
      return application.toLowerCase();
    }
}
